import {useMemo, useRef, type ReactNode, type RefObject} from "react";
import type {ListModuleConfig} from "~/domainModels/listView/listModuleConfig";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotListCardFooterBindings} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import {
    navigateToScheduleCallRoute,
    type ScheduleCallNavigateFn,
} from "~/components/scheduleCall/navigateToScheduleCallRoute";
import {useScheduleCallEngagement} from "~/components/scheduleCall/useScheduleCallEngagement";
import {useWorkspace} from "~/contexts/workspaceContext";
import {resolveWorkspaceLogCallRoute} from "~/domainModels/scheduleCall/resolveWorkspaceLogCallRoute";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {dismissDataViewerAsidePanels} from "~/components/ottopilotDataViewer/rowEngagement/dismissDataViewerAsidePanels";
import {resolveEventRowEngagementCapabilities} from "~/components/ottopilotDataViewer/rowEngagement/resolveEventRowEngagementCapabilities";
import type {RowEngagementHandlers} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementHandlers";
import {useEventArchiveEngagement} from "~/components/ottopilotDataViewer/rowEngagement/useEventArchiveEngagement";
import type {ListNotesPanelOpenContext} from "~/components/notes/listNotesPanelTypes";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {
    EventContainerConstraint,
    EventContainerScheduleCallDisableConfig,
} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    isEventContainerEngagementRestrictedByGlobalConstraints,
    isEventContainerRowActionDisabledByGlobalConstraints,
} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import {getScheduleCallDisabledReason} from "~/domainModels/workspaceModules/eventContainerScheduleCallDisable";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {buildEventContainerProfileFromForEventTemplate} from "~/utilities/eventContainerProfileNavigation";

export function useEventRowEngagement({
    workspaceId,
    navigate,
    listModuleConfig,
    showDisposition,
    showNotes,
    includeCreateDrawer,
    listCreateDrawerItemsCount,
    scheduleRoute,
    onScheduleSuccess,
    queryKeyPrefix,
    controllerRef,
    dispositionEventId,
    notesPanelEventContainerId,
    openNotesPanel,
    closeNotesPanel,
    notesEventContainerSourceLabel,
    isCreateDrawerOpen,
    onOpenDisposition,
    onCloseDisposition,
    onCreateDrawerOpenChange,
    archiveMenuItemLabel,
    renderMoreMenuItems,
    isArchivedView,
    pageEventTemplate,
    globalConstraints,
    scheduleCallDisableConfig,
    afterArchiveInvalidate,
}: {
    workspaceId: Uuid;
    navigate: ScheduleCallNavigateFn;
    isArchivedView: boolean;
    /** Optional workspace config; parent journey constraints disable all but view/archive/profile. */
    globalConstraints?: Array<EventContainerConstraint>;
    scheduleCallDisableConfig?: EventContainerScheduleCallDisableConfig;
    listModuleConfig: ListModuleConfig | null;
    showDisposition: boolean;
    showNotes: boolean;
    includeCreateDrawer: boolean;
    listCreateDrawerItemsCount: number;
    scheduleRoute?: string;
    onScheduleSuccess?: () => void;
    queryKeyPrefix: Array<unknown>;
    controllerRef: RefObject<OttopilotDataViewerController<EventWithEventContainer> | null>;
    dispositionEventId: Uuid | null;
    notesPanelEventContainerId: Uuid | null;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    closeNotesPanel: () => void;
    notesEventContainerSourceLabel?: string;
    isCreateDrawerOpen: boolean;
    onOpenDisposition: (event: EventWithEventContainer) => void;
    onCloseDisposition: () => void;
    onCreateDrawerOpenChange: (open: boolean) => void;
    archiveMenuItemLabel?: string;
    renderMoreMenuItems?: (event: EventWithEventContainer) => ReactNode;
    pageEventTemplate?: OttopilotEventTemplate;
    afterArchiveInvalidate?: () => Promise<void>;
}) {
    const {workspace, workspaceCapabilities} = useWorkspace();
    const footerBindings = useOttopilotListCardFooterBindings();
    const archiveEngagement = useEventArchiveEngagement({
        queryKeyPrefix,
        archiveMenuItemLabel,
        controllerRef,
        pageEventTemplate,
        afterInvalidate: afterArchiveInvalidate,
    });
    const scheduleCall = useScheduleCallEngagement({
        navigate,
        workspaceId,
        scheduleRoute,
        onSuccess: onScheduleSuccess,
        scheduleCallDisableConfig,
    });
    const logCallRoute = useMemo(
        () => resolveWorkspaceLogCallRoute(workspace, workspaceCapabilities),
        [workspace, workspaceCapabilities],
    );
    const hasLogCallAction = logCallRoute.trim() !== "";

    const capabilities = useMemo(
        () =>
            resolveEventRowEngagementCapabilities({
                listConfig: listModuleConfig,
                showDisposition,
                showNotes,
                includeCreateDrawer,
                listCreateDrawerItemsCount,
                hasScheduleAction: scheduleCall.hasScheduleAction,
                hasLogCallAction,
                isArchiveAvailable: archiveEngagement.isArchiveAvailable,
                showProfile: true,
                isArchivedView,
            }),
        [
            listModuleConfig,
            showDisposition,
            showNotes,
            includeCreateDrawer,
            listCreateDrawerItemsCount,
            scheduleCall.hasScheduleAction,
            hasLogCallAction,
            archiveEngagement.isArchiveAvailable,
            isArchivedView,
        ],
    );

    const handlersRef = useRef({
        footerBindings,
        onOpenDisposition,
        onCloseDisposition,
        onCreateDrawerOpenChange,
        openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel,
        openScheduleCall: scheduleCall.openScheduleCall,
        archiveEngagement,
        logCallRoute,
        navigate,
        workspaceId,
    });
    handlersRef.current = {
        footerBindings,
        onOpenDisposition,
        onCloseDisposition,
        onCreateDrawerOpenChange,
        openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel,
        openScheduleCall: scheduleCall.openScheduleCall,
        archiveEngagement,
        logCallRoute,
        navigate,
        workspaceId,
    };

    const dismissAsidePanels = (options?: {keepNotesPanel?: boolean}) => {
        const b = handlersRef.current.footerBindings;
        dismissDataViewerAsidePanels({
            controllerRef,
            closeCreateDrawer: () => {
                b.setSelectedCreateJourneyId(null);
                handlersRef.current.onCreateDrawerOpenChange(false);
            },
            closeNotesPanel:
                options?.keepNotesPanel === true ? undefined : handlersRef.current.closeNotesPanel,
            closeDisposition: handlersRef.current.onCloseDisposition,
            closeScheduleCall: scheduleCall.closeScheduleCallModal,
            dismissArchive: handlersRef.current.archiveEngagement.dismissArchive,
        });
    };

    const handlers: RowEngagementHandlers<EventWithEventContainer> = useMemo(
        () => ({
            openDisposition: (event) => {
                dismissAsidePanels();
                handlersRef.current.onOpenDisposition(event);
            },
            openCreate: (event) => {
                dismissAsidePanels();
                const b = handlersRef.current.footerBindings;
                b.setSelectedCreateJourneyId(event.eventContainer.id);
                handlersRef.current.onCreateDrawerOpenChange(true);
            },
            openLogCall: (event) => {
                dismissAsidePanels();
                const route = handlersRef.current.logCallRoute.trim();
                if (route === "") {
                    return;
                }
                navigateToScheduleCallRoute(
                    handlersRef.current.navigate,
                    route,
                    handlersRef.current.workspaceId,
                    event.eventContainer.id,
                );
            },
            openNotes: (event) => {
                dismissAsidePanels({keepNotesPanel: true});
                handlersRef.current.openNotesPanel({
                    eventContainerId: event.eventContainer.id,
                    eventContainer: event.eventContainer,
                    createScope: {
                        scopeType: "event",
                        eventId: event.id,
                        eventTemplateId: event.templateId,
                    },
                    eventContainerSourceLabel: handlersRef.current.notesEventContainerSourceLabel ?? "Journey",
                });
            },
            openSchedule: (event) => {
                dismissAsidePanels();
                handlersRef.current.openScheduleCall({...event.eventContainer, events: []});
            },
        }),
        [],
    );

    const profileNavigationFrom =
        pageEventTemplate === undefined ? undefined : buildEventContainerProfileFromForEventTemplate(pageEventTemplate.id);

    const tableRowActions: TableRowActionsConfig<EventWithEventContainer> = useMemo(
        () => ({
            toolbar: capabilities.toolbar,
            moreMenu: capabilities.moreMenu,
            onProfile: (event) => {
                const controller = controllerRef.current;
                if (controller === null) {
                    return;
                }
                dismissAsidePanels();
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.read,
                    rowEngagementPanelType: RowEngagementPanelType.sidebar,
                    rowEngagementData: event,
                    readPanelLayout: "entityProfile",
                    hidePanelLayoutToggle: true,
                });
            },
            onDisposition: handlers.openDisposition,
            onEdit: (event) => {
                const controller = controllerRef.current;
                if (controller === null || controller.canEditRow?.(event) === false) {
                    return;
                }
                dismissAsidePanels();
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.update,
                    rowEngagementPanelType:
                        controller.updatePanelOpensInFullscreen === true
                            ? RowEngagementPanelType.fullscreen
                            : RowEngagementPanelType.sidebar,
                    rowEngagementData: event,
                });
            },
            onView: (event) => {
                const controller = controllerRef.current;
                if (controller === null) {
                    return;
                }
                dismissAsidePanels();
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.read,
                    rowEngagementPanelType: RowEngagementPanelType.sidebar,
                    rowEngagementData: event,
                });
            },
            onCreate: handlers.openCreate,
            onLogCall: handlers.openLogCall,
            onNotes: handlers.openNotes,
            onScheduleCall: handlers.openSchedule,
            onArchive: (event) => {
                dismissAsidePanels();
                archiveEngagement.requestArchive(event);
            },
            canEdit: (row) => {
                if (isEventContainerRowActionDisabledByGlobalConstraints("edit", row.eventContainer, globalConstraints) === true) {
                    return false;
                }
                return controllerRef.current?.canEditRow?.(row) ?? true;
            },
            isToolbarItemDisabled: (item, event) =>
                isEventContainerRowActionDisabledByGlobalConstraints(item, event.eventContainer, globalConstraints),
            isMenuItemDisabled: (item, event) => {
                if (isEventContainerRowActionDisabledByGlobalConstraints(item, event.eventContainer, globalConstraints) === true) {
                    return true;
                }
                if (item === "archive") {
                    if (isEventContainerEngagementRestrictedByGlobalConstraints(event.eventContainer, globalConstraints) === true) {
                        return false;
                    }
                    return archiveEngagement.getArchiveDisabledReason(event) !== null;
                }
                if (item === "scheduleCall") {
                    return getScheduleCallDisabledReason(event.eventContainer, scheduleCallDisableConfig) !== null;
                }
                return false;
            },
            renderMoreMenuItems: isArchivedView === true ? undefined : renderMoreMenuItems,
            getActiveState: (event) => {
                const controller = controllerRef.current;
                if (controller === null) {
                    return {};
                }
                const panel = controller.currentlyOpenRowEngagementPanel;
                const bindings = footerBindings;
                const readOpen =
                    panel.rowEngagementType === RowEngagementType.read &&
                    panel.rowEngagementData?.id === event.id;
                const isEntityProfileAside =
                    readOpen === true && panel.readPanelLayout === "entityProfile";
                const view = readOpen === true && isEntityProfileAside !== true;
                const edit =
                    panel.rowEngagementType === RowEngagementType.update &&
                    panel.rowEngagementData?.id === event.id;
                const create =
                    isCreateDrawerOpen === true && bindings.selectedCreateJourneyId === event.eventContainer.id;
                const notes =
                    notesPanelEventContainerId === event.eventContainer.id;
                const scheduleCallActive =
                    scheduleCall.activeJourneyId != null && scheduleCall.activeJourneyId === event.eventContainer.id;
                const archive = archiveEngagement.archiveTargetEventId === event.id;
                const more =
                    capabilities.toolbar.includes("more") === true &&
                    (edit || create || notes || scheduleCallActive || archive);

                return {
                    disposition: dispositionEventId === event.id,
                    edit,
                    view,
                    profile: isEntityProfileAside,
                    create,
                    notes,
                    scheduleCall: scheduleCallActive,
                    archive,
                    more,
                };
            },
        }),
        [
            capabilities,
            controllerRef,
            footerBindings,
            handlers,
            archiveEngagement,
            dispositionEventId,
            notesPanelEventContainerId,
            scheduleCall.activeJourneyId,
            isCreateDrawerOpen,
            isArchivedView,
            renderMoreMenuItems,
            globalConstraints,
            scheduleCallDisableConfig,
        ],
    );

    return {
        handlers,
        footerBindings,
        tableRowActions,
        archiveModals: archiveEngagement.archiveModals,
        scheduleCallModal: scheduleCall.scheduleCallModal,
        profileNavigationFrom,
    };
}
